/**
 * Platform abstraction — a thin seam between code that runs in
 * different environments. Today there's exactly one environment
 * (web, served from Vercel). When the Capacitor wrap lands as its
 * own phase, native branches get filled in here without touching
 * call sites.
 *
 * The boundary is deliberately small: only the handful of operations
 * that have different correct implementations on web vs. native.
 *
 *   - canonicalOrigin() — the public-web origin used for shareable
 *     URLs. On native, window.location.origin returns the in-app
 *     scheme (e.g. capacitor://localhost), which is useless to share
 *     with someone outside the app.
 *
 *   - apiBaseUrl() — prefix for fetch('/api/...') calls. Empty on
 *     web (relative paths resolve against the same origin). On
 *     native, the bundle is served from capacitor://localhost, so
 *     a relative /api path tries to hit the device itself — must
 *     be rewritten to an absolute URL pointing at the deployed
 *     Vercel functions.
 *
 *   - openExternal(url) — opens a URL outside the app. On web this
 *     is window.location.href (mailto:) or window.open (http). On
 *     native this needs the Capacitor App / Browser plugin so the
 *     OS handles the URL instead of trying to navigate inside the
 *     webview.
 *
 *   - getRoutePath() — the current route path used for in-app
 *     dispatch (e.g. detecting /game/:id deep-links). On web this
 *     is window.location.pathname. On native, deep links arrive via
 *     the Capacitor App plugin's appUrlOpen event and need to be
 *     parsed there.
 *
 * Web implementation is inlined below. Native flesh-out is gated
 * behind isCapacitor() — false today, will detect window.Capacitor
 * once the wrap is added.
 */

function isCapacitor(): boolean {
  // Until the Capacitor wrap exists, this is always false. The
  // detection check is left in place so the native branches below
  // become live the moment @capacitor/core injects its global.
  return false;
}

/**
 * Public web origin for shareable URLs. Use this anywhere you'd
 * otherwise reach for `window.location.origin` to build a link
 * that another person will click.
 */
export function canonicalOrigin(): string {
  if (isCapacitor()) {
    // TODO(native): return a build-time constant like
    // import.meta.env.VITE_CANONICAL_ORIGIN. capacitor://localhost
    // is NEVER the right answer for a shareable link.
    throw new Error('canonicalOrigin: native branch not implemented yet');
  }
  return window.location.origin;
}

/**
 * Base URL to prepend to `/api/...` fetch paths. Empty string on
 * web (relative paths Just Work). Absolute origin on native.
 *
 * Call sites should do: `fetch(\`${apiBaseUrl()}/api/games/...\`)`.
 * The template-literal pattern keeps the call site readable in
 * both environments — '' + '/api/x' = '/api/x' on web, and
 * 'https://...' + '/api/x' = 'https://.../api/x' on native.
 */
export function apiBaseUrl(): string {
  if (isCapacitor()) {
    // TODO(native): return the deployed web origin from a build-time
    // constant. The bundle inside the native app needs to talk to
    // the same Vercel Functions the web app uses.
    throw new Error('apiBaseUrl: native branch not implemented yet');
  }
  return '';
}

/**
 * Open a URL outside the app. mailto:, tel:, https:// — anything
 * the OS should handle in its own browser/mail client rather than
 * inside our canvas. On web, this navigates the current tab for
 * mailto/tel (where opening a new tab would be wasted) and opens a
 * new tab for https.
 */
export function openExternal(url: string): void {
  if (isCapacitor()) {
    // TODO(native): use the Capacitor App / Browser plugin so
    // mailto: hits the OS mail app and https opens in the in-app
    // browser instead of trying to navigate the webview off our
    // bundle.
    throw new Error('openExternal: native branch not implemented yet');
  }
  // mailto: / tel: are protocol handlers — same-window navigation
  // hands off cleanly. http(s) opens a new tab so the game stays
  // alive in the original one (single-page-app convention).
  if (url.startsWith('mailto:') || url.startsWith('tel:')) {
    window.location.href = url;
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Current route path for in-app dispatch. On web, this is just
 * window.location.pathname. On native, deep links arrive through
 * a different channel and this function would read whichever
 * value was cached from the most recent appUrlOpen event.
 */
export function getRoutePath(): string {
  if (isCapacitor()) {
    // TODO(native): read from a module-level cache populated by
    // the Capacitor App plugin's appUrlOpen listener at boot.
    throw new Error('getRoutePath: native branch not implemented yet');
  }
  return window.location.pathname;
}
